import { useCallback, useRef } from 'react';

/** The innermost element from `target` up to `body` that actually scrolls vertically, if any. */
function findScroller(target: EventTarget | null, body: HTMLElement): HTMLElement | null {
  for (let el = target as HTMLElement | null; el; el = el.parentElement) {
    if (el.scrollHeight > el.clientHeight + 1 && /auto|scroll/.test(getComputedStyle(el).overflowY)) return el;
    if (el === body) break;
  }
  return null;
}

/**
 * Ref callback for the body of a bottom sheet (an IonModal with breakpoints) that scrolls inside itself.
 *
 * Ionic only keeps a sheet's drag gesture off its content when the scroll area is an <ion-content>. A sheet that
 * scrolls a plain div / IonList instead has every scroll taken over by the sheet's drag: the list can't scroll and
 * the sheet closes. This gives such a sheet the behavior of an iOS sheet:
 *  - a touch on a scrolling area scrolls it — the sheet never sees the moves;
 *  - only a touch that starts with that area already at its top AND moves DOWN is handed to the sheet, so pulling
 *    down from the top closes it, and a scroll that merely runs up to the top never does.
 * The direction is decided on the first move of each touch. Areas that don't scroll (short lists) always hand the
 * touch to the sheet, so they can still be swiped away.
 *
 * Put it on the element that wraps the sheet's scroll area (the sheet's header can stay draggable):
 *   const sheetBodyRef = useSheetScrollGuard();
 *   <IonCardContent ref={sheetBodyRef}>…</IonCardContent>
 */
export function useSheetScrollGuard() {
  const removeRef = useRef<(() => void) | null>(null);

  return useCallback((body: HTMLElement | null) => {
    removeRef.current?.();
    removeRef.current = null;
    if (!body) return;

    let scroller: HTMLElement | null = null;
    let startY = 0;
    // null = not decided yet for this touch
    let keepFromSheet: boolean | null = null;

    const onTouchStart = (e: TouchEvent) => {
      scroller = findScroller(e.target, body);
      startY = e.touches[0]?.clientY ?? 0;
      keepFromSheet = scroller ? null : false;
    };
    const onTouchMove = (e: TouchEvent) => {
      if (keepFromSheet === null && scroller) {
        const dy = (e.touches[0]?.clientY ?? startY) - startY;
        if (dy === 0) return;
        keepFromSheet = !(dy > 0 && scroller.scrollTop <= 0);
      }
      if (keepFromSheet) e.stopPropagation();
    };
    // Mouse (desktop): there is no touch scrolling to protect, just don't drag the sheet from a scrolled list
    const onMouseDown = (e: MouseEvent) => {
      const el = findScroller(e.target, body);
      if (el && el.scrollTop > 0) e.stopPropagation();
    };

    body.addEventListener('touchstart', onTouchStart, { passive: true });
    body.addEventListener('touchmove', onTouchMove, { passive: true });
    body.addEventListener('mousedown', onMouseDown);
    removeRef.current = () => {
      body.removeEventListener('touchstart', onTouchStart);
      body.removeEventListener('touchmove', onTouchMove);
      body.removeEventListener('mousedown', onMouseDown);
    };
  }, []);
}
